import React, {useState, useEffect} from 'react'
import {Usuario, CreateUsuarioData, UpdateUsuarioData, Rol, Funcionalidad} from '../core/models'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'
import {KTIcon} from '../../../../_metronic/helpers'
import RolesSelector from './RolesSelector'
import FuncionalidadesSelector from './FuncionalidadesSelector'

interface Props {
  onSubmit: (usuario: CreateUsuarioData | UpdateUsuarioData) => void
  loading?: boolean
  initialData?: Usuario
  isEditMode?: boolean
  roles: Rol[]
  funcionalidades: Funcionalidad[]
  usuarios: Usuario[]
  usuarioId?: number
}

export const CreateUsuarioForm: React.FC<Props> = ({
  onSubmit,
  loading = false,
  initialData,
  isEditMode = false,
  roles = [],
  funcionalidades = [],
  usuarios = [],
  usuarioId,
}) => {
  const [formData, setFormData] = useState<CreateUsuarioData>({
    email: '',
    nombre: '',
    apellido: '',
    password: '',
    es_admin: 0,
    activo: 1,
    supervisor_id: null,
  })

  const [selectedRoles, setSelectedRoles] = useState<number[]>([])
  const [selectedFuncionalidades, setSelectedFuncionalidades] = useState<number[]>([])
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Cargar datos iniciales si está en modo edición
  useEffect(() => {
    if (initialData) {
      setFormData({
        email: initialData.email,
        nombre: initialData.nombre,
        apellido: initialData.apellido,
        password: '', // No cargamos la contraseña
        es_admin: initialData.es_admin,
        activo: initialData.activo,
        supervisor_id: initialData.supervisor_id,
      })
      
      // Mapear roles: si vienen como IDs, usar directamente; si vienen como objetos, extraer el ID
      const rolesIds = initialData.roles
        ? initialData.roles.map((r) => (typeof r === 'number' ? r : r.id))
        : []
      
      // Mapear funcionalidades: si vienen como IDs, usar directamente; si vienen como objetos, extraer el ID
      const funcionalidadesIds = initialData.funcionalidades
        ? initialData.funcionalidades.map((f) => (typeof f === 'number' ? f : f.id))
        : []
      
      setSelectedRoles(rolesIds)
      setSelectedFuncionalidades(funcionalidadesIds)
    }
  }, [initialData])

  // Los roles y funcionalidades ya vienen en initialData desde getUsuario
  // No necesitamos cargarlos por separado

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value, type} = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? ((e.target as HTMLInputElement).checked ? 1 : 0) : value,
    }))
  }

  const handleSupervisorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      supervisor_id: value === '' ? null : parseInt(value),
    }))
  }


  const handlePasswordChange = async () => {
    if (!usuarioId || !newPassword) {
      alert('Por favor ingresa una nueva contraseña')
      return
    }

    if (newPassword !== confirmPassword) {
      alert('Las contraseñas no coinciden')
      return
    }

    try {
      setPasswordLoading(true)
      const token = localStorage.getItem('X-TOKEN')
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'setUsuarioPassword',
          id: usuarioId,
          password: newPassword,
        }),
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()

      if (data.code === 'OK') {
        alert('Contraseña actualizada correctamente')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        throw new Error(data.returned_message || 'Error al actualizar la contraseña')
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setPasswordLoading(false)
    }
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isEditMode) {
      // Actualizar roles y funcionalidades antes de actualizar el usuario
      if (usuarioId) {
        try {
          const token = localStorage.getItem('X-TOKEN')
          if (!token) {
            throw new Error('No se encontró el token de autenticación')
          }

          // Actualizar roles
          const rolesResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-TOKEN': token,
            },
            body: JSON.stringify({
              service: 'setUsuarioRoles',
              id: usuarioId,
              roles: selectedRoles,
            }),
          })

          if (!rolesResponse.ok) {
            throw new Error(`Error en la petición de roles: ${rolesResponse.status}`)
          }

          const rolesData = await rolesResponse.json()
          if (rolesData.code !== 'OK') {
            throw new Error(rolesData.returned_message || 'Error al actualizar los roles')
          }

          // Actualizar funcionalidades
          const funcionalidadesResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-TOKEN': token,
            },
            body: JSON.stringify({
              service: 'setUsuarioFuncionalidades',
              id: usuarioId,
              funcionalidades: selectedFuncionalidades,
            }),
          })

          if (!funcionalidadesResponse.ok) {
            throw new Error(`Error en la petición de funcionalidades: ${funcionalidadesResponse.status}`)
          }

          const funcionalidadesData = await funcionalidadesResponse.json()
          if (funcionalidadesData.code !== 'OK') {
            throw new Error(
              funcionalidadesData.returned_message || 'Error al actualizar las funcionalidades'
            )
          }
        } catch (err) {
          console.error('Error al actualizar roles o funcionalidades:', err)
          alert(err instanceof Error ? err.message : 'Error al actualizar roles o funcionalidades')
          return // No continuar si hay error
        }
      }

      // Actualizar datos del usuario
      const updateData: UpdateUsuarioData = {
        id: usuarioId!,
        nombre: formData.nombre,
        apellido: formData.apellido,
        es_admin: formData.es_admin,
        supervisor_id: formData.supervisor_id,
      }
      onSubmit(updateData)
    } else {
      if (!formData.password) {
        alert('Por favor ingresa una contraseña')
        return
      }
      onSubmit(formData)
    }
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>{isEditMode ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</h3>
      </div>
      <div className='card-body'>
        <form onSubmit={handleSubmit}>
          {/* Información básica */}
          <div className='separator separator-dashed my-5'></div>
          <h4 className='mb-4'>
            <KTIcon iconName='user' className='fs-3 me-2' />
            Información Básica
          </h4>

          <div className='row mb-5'>
            <div className='col-md-6'>
              <label className='form-label required'>Nombre</label>
              <input
                type='text'
                className='form-control'
                name='nombre'
                value={formData.nombre}
                onChange={handleChange}
                required
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label required'>Apellido</label>
              <input
                type='text'
                className='form-control'
                name='apellido'
                value={formData.apellido}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className='row mb-5'>
            <div className='col-md-6'>
              <label className='form-label required'>Email</label>
              <input
                type='email'
                className='form-control'
                name='email'
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isEditMode}
              />
              {isEditMode && <div className='form-text'>El email no se puede modificar</div>}
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Supervisor</label>
              <select
                className='form-select'
                value={formData.supervisor_id || ''}
                onChange={handleSupervisorChange}
              >
                <option value=''>Sin supervisor</option>
                {usuarios
                  .filter((u) => !usuarioId || u.id !== usuarioId)
                  .map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nombre} {usuario.apellido}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Contraseña */}
          {!isEditMode && (
            <div className='row mb-5'>
              <div className='col-md-6'>
                <label className='form-label required'>Contraseña</label>
                <input
                  type='password'
                  className='form-control'
                  name='password'
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          )}

          {/* Cambio de contraseña (solo en edición) */}
          {isEditMode && (
            <>
              <div className='separator separator-dashed my-5'></div>
              <h4 className='mb-4'>
                <KTIcon iconName='lock' className='fs-3 me-2' />
                Cambiar Contraseña
              </h4>
              <div className='row mb-5'>
                <div className='col-md-6'>
                  <label className='form-label'>Nueva Contraseña</label>
                  <input
                    type='password'
                    className='form-control'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>Confirmar Contraseña</label>
                  <input
                    type='password'
                    className='form-control'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className='mb-5'>
                <button
                  type='button'
                  className='btn btn-light-primary'
                  onClick={handlePasswordChange}
                  disabled={passwordLoading || !newPassword || !confirmPassword}
                >
                  {passwordLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='check' className='fs-3 me-1' />
                      Actualizar Contraseña
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Configuración */}
          <div className='separator separator-dashed my-5'></div>
          <h4 className='mb-4'>
            <KTIcon iconName='setting-2' className='fs-3 me-2' />
            Configuración
          </h4>

          <div className='row mb-5'>
            <div className='col-12'>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='es_admin'
                  name='es_admin'
                  checked={formData.es_admin === 1}
                  onChange={(e) =>
                    setFormData((prev) => ({...prev, es_admin: e.target.checked ? 1 : 0}))
                  }
                />
                <label className='form-check-label fw-semibold' htmlFor='es_admin'>
                  <div className='d-flex align-items-center'>
                    <KTIcon iconName='shield-tick' className='fs-2 text-warning me-2' />
                    <div>
                      <div className='text-gray-800'>Usuario Administrador</div>
                      <div className='text-muted fs-7'>
                        Tiene permisos de administrador en el sistema
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {!isEditMode && (
            <div className='row mb-5'>
              <div className='col-12'>
                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='activo'
                    name='activo'
                    checked={formData.activo === 1}
                    onChange={(e) =>
                      setFormData((prev) => ({...prev, activo: e.target.checked ? 1 : 0}))
                    }
                  />
                  <label className='form-check-label fw-semibold' htmlFor='activo'>
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='verify' className='fs-2 text-success me-2' />
                      <div>
                        <div className='text-gray-800'>Usuario Activo</div>
                        <div className='text-muted fs-7'>
                          El usuario está habilitado y puede acceder al sistema
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Roles (solo en edición) */}
          {isEditMode && (
            <>
              <div className='separator separator-dashed my-5'></div>
              <RolesSelector
                selectedRoles={selectedRoles}
                availableRoles={roles}
                loading={false}
                onChange={setSelectedRoles}
              />
            </>
          )}

          {/* Funcionalidades (solo en edición) */}
          {isEditMode && (
            <>
              <div className='separator separator-dashed my-5'></div>
              <FuncionalidadesSelector
                selectedFuncionalidades={selectedFuncionalidades}
                availableFuncionalidades={funcionalidades}
                loading={false}
                onChange={setSelectedFuncionalidades}
              />
            </>
          )}

          {/* Botones de acción */}
          <div className='separator separator-dashed my-5'></div>
          <div className='d-flex justify-content-end'>
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {loading ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  {isEditMode ? 'Actualizando...' : 'Creando...'}
                </>
              ) : (
                <>
                  <KTIcon iconName={isEditMode ? 'check' : 'plus'} className='fs-3 me-1' />
                  {isEditMode ? 'Actualizar Usuario' : 'Crear Usuario'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
